import { css, html, nothing, type PropertyValues } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { when } from 'lit/directives/when.js'
import { HostAriaLabelMixin, SchmancyFormField } from '@schmancy/mixins/index'
import '@schmancy/primitives/button/icon-button'
import '@schmancy/primitives/typography/typography'

export type SchmancyQuantityChangeEvent = CustomEvent<{ value: number }>

/**
 * @element schmancy-quantity
 *
 * The discrete-quantity stepper: decrement icon-button, numeric readout,
 * increment icon-button. It is the constrained-count control — a basket line's
 * quantity, a seat count, a per-item multiplier — where `<schmancy-input
 * type="number">` is free-typed numeric entry and `<schmancy-range>` /
 * `<schmancy-slider>` are continuous-value controls.
 *
 * Form-associated: it participates in `FormData`, answers `checkValidity()`
 * with the platform's own range flags, and resets with its owning form.
 *
 * The readout carries `role="spinbutton"`, so a keyboard user drives the value
 * without reaching the buttons: ArrowUp / ArrowRight step up, ArrowDown /
 * ArrowLeft step down, Home lands on `min`, End on the highest value `step`
 * actually reaches at or below `max`.
 *
 * @fires change - `CustomEvent<{ value: number }>` on every user-driven change.
 * @csspart control - The three-cell row holding both buttons and the readout.
 * @csspart readout - The `role="spinbutton"` element carrying the value.
 */
@customElement('schmancy-quantity')
export class SchmancyQuantity extends HostAriaLabelMixin(SchmancyFormField(css`
	:host {
		display: inline-flex;
		flex-direction: column;
		flex-shrink: 0;
		/* A grid or flex item stretches to its track unless it carries a size of
		   its own, and inline-flex is blockified away the moment it becomes one —
		   the control grid then inherits the column and stretches its auto tracks,
		   putting the minus and plus at opposite edges. schmancy-badge declares
		   the same intrinsic measure for the same reason; it settles the size
		   inside the component instead of overriding the ancestor's alignment.
		   Measured in quantity.test.ts: 240px and 600px columns, host 240/600
		   before, its own 144px content measure at both after. */
		width: fit-content;
	}

	:host([disabled]) {
		pointer-events: none;
	}
`)) {
	// `formAssociated`, `internals`, `name`, `label`, `required`, `disabled`,
	// `readonly`, `error`, `validationMessage`, `hint`, `validateOn`,
	// touched/dirty/submitted, `markTouched`, `resetForm`, `toFormEntries`, and
	// the FIELD_CONNECT_EVENT handshake all come from the mixin.

	/** Numeric value — narrowed override of the mixin's wide value union. */
	@property({ type: Number, reflect: true })
	override value: number = 0

	/** Lowest reachable value. The decrement button disables here. */
	@property({ type: Number }) min: number = 0

	/**
	 * Highest allowed value. Unbounded by default, so the increment button only
	 * ever disables once a consumer declares a ceiling.
	 */
	@property({ type: Number }) max: number = Infinity

	/** Amount one press moves the value. */
	@property({ type: Number }) step: number = 1

	/**
	 * Unit rendered beside the numeral — "bottle", "kg", "×". Purely the
	 * readout's own trailing glyph; it never reaches `FormData`.
	 */
	@property({ type: String }) unit?: string

	/** Value snapshot taken at first render, restored by `resetForm()`. */
	private quantityDefault: number = 0

	/**
	 * The highest value `step` actually lands on at or below `max`. A max of 10
	 * with a step of 3 tops out at 9, never at an off-grid 10, so the End key
	 * and the increment end-stop agree with what pressing `+` can reach.
	 */
	private get reachableMax(): number {
		return this.min + Math.floor((this.max - this.min) / this.step) * this.step
	}

	override firstUpdated(changed: PropertyValues): void {
		super.firstUpdated(changed)
		this.quantityDefault = this.value
	}

	override willUpdate(changed: PropertyValues): void {
		super.willUpdate(changed)
		if (changed.has('value') || changed.has('name')) {
			this.internals?.setFormValue(String(this.value))
		}
		if (changed.has('min') || changed.has('max') || changed.has('step')) {
			this.checkValidity()
		}
	}

	/**
	 * Range and step are the control's contract, not decoration, so each
	 * surfaces as the platform flag a native `<input type="number">` would set.
	 * `required` on a counter means "at least one", the same reading the
	 * mixin's `value === ''` emptiness test cannot express for a numeric field.
	 */
	override checkValidity(): boolean {
		const flags: ValidityStateFlags = {}
		let message = ''
		if (this.required && this.value <= 0) {
			flags.valueMissing = true
			message = this.errorMessages?.valueMissing ?? 'Choose at least one.'
		} else if (this.value < this.min) {
			flags.rangeUnderflow = true
			message = this.errorMessages?.rangeUnderflow ?? `The lowest allowed is ${this.min}.`
		} else if (this.value > this.max) {
			flags.rangeOverflow = true
			message = this.errorMessages?.rangeOverflow ?? `The highest allowed is ${this.max}.`
		} else if (Math.abs(this.value - (this.min + Math.round((this.value - this.min) / this.step) * this.step)) > 1e-9) {
			flags.stepMismatch = true
			message = this.errorMessages?.stepMismatch ?? `Only multiples of ${this.step} are allowed.`
		}

		return this.setFieldValidity(flags, message)
	}

	override resetForm(): void {
		super.resetForm()
		// The mixin restores `defaultValue ?? ''`; an empty string is not a
		// value this field's model can hold, so the numeric snapshot is
		// re-asserted after it.
		this.value = this.quantityDefault
	}

	override formStateRestoreCallback(state: string | File | FormData | null): void {
		if (typeof state !== 'string') return
		const restored = Number(state)
		if (Number.isFinite(restored)) this.value = restored
	}

	private commit(raw: number): void {
		if (this.disabled || this.readonly) return
		// Land on the step grid before clamping, so a fractional step never
		// accumulates binary-float dust across repeated presses.
		const snapped = this.min + Math.round((raw - this.min) / this.step) * this.step
		const next = Math.min(this.reachableMax, Math.max(this.min, Number(snapped.toFixed(10))))
		if (next === this.value) return
		this.value = next
		this.markTouched()
		this.emitChange({ value: this.value })
	}

	private onKeydown = (e: KeyboardEvent) => {
		switch (e.key) {
			case 'ArrowUp':
			case 'ArrowRight':
				e.preventDefault()
				this.commit(this.value + this.step)
				break
			case 'ArrowDown':
			case 'ArrowLeft':
				e.preventDefault()
				this.commit(this.value - this.step)
				break
			case 'Home':
				e.preventDefault()
				this.commit(this.min)
				break
			case 'End':
				e.preventDefault()
				this.commit(this.reachableMax)
				break
		}
	}

	render() {
		const inert = this.disabled || this.readonly
		const name = this.ariaLabel || this.label || 'Quantity'
		// aria-labelledby outranks aria-label, so the visible label names the
		// readout only while no aria-label was authored over it.
		const labelledBy = this.label && !this.ariaLabel
		const valueText = this.unit ? `${this.value} ${this.unit}` : String(this.value)

		return html`
			${when(
				this.label,
				() => html`
					<schmancy-typography id="${this.id}-label" type="label" token="lg" class="mb-1">
						${this.label}
					</schmancy-typography>
				`,
			)}

			<div
				part="control"
				class=${this.classMap({
					'inline-grid grid-flow-col items-center gap-1 rounded-full': true,
					// `outline-solid`, never a bare `outline` — the bare utility emits no
					// outline-style in the baked shadow-DOM sheet, so width and color
					// resolve while the ring stays invisible.
					'outline-solid outline-2 outline-offset-2 outline-error-default': this.error,
					'opacity-[var(--schmancy-sys-state-opacity-disabled,0.38)]': this.disabled,
				})}
			>
				<schmancy-icon-button
					icon="remove"
					color="neutral"
					aria-label="Decrease ${name}"
					?disabled=${inert || this.value <= this.min}
					@click=${() => this.commit(this.value - this.step)}
				></schmancy-icon-button>

				<div
					part="readout"
					role="spinbutton"
					tabindex=${this.disabled ? -1 : 0}
					aria-labelledby=${ifDefined(labelledBy ? `${this.id}-label` : undefined)}
					aria-label=${ifDefined(labelledBy ? undefined : name)}
					aria-valuenow=${this.value}
					aria-valuemin=${this.min}
					aria-valuemax=${ifDefined(Number.isFinite(this.max) ? this.reachableMax : undefined)}
					aria-valuetext=${valueText}
					aria-invalid=${this.error ? 'true' : 'false'}
					aria-required=${this.required ? 'true' : 'false'}
					aria-disabled=${this.disabled ? 'true' : 'false'}
					aria-readonly=${this.readonly ? 'true' : 'false'}
					class="grid grid-flow-col items-baseline justify-center gap-1 min-w-10 px-1 rounded-full outline-hidden focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary-default"
					@keydown=${this.onKeydown}
					@blur=${() => this.markTouched()}
				>
					<!-- 22px numeral against a 14px unit is the pair on the type-scale
					     grid closest to the golden ratio at readout magnitude (1.57). -->
					<schmancy-typography type="title" token="lg" class="tabular-nums">${this.value}</schmancy-typography>
					${when(this.unit, () => html`<schmancy-typography type="label" token="lg">${this.unit}</schmancy-typography>`)}
				</div>

				<schmancy-icon-button
					icon="add"
					color="neutral"
					aria-label="Increase ${name}"
					?disabled=${inert || this.value >= this.reachableMax}
					@click=${() => this.commit(this.value + this.step)}
				></schmancy-icon-button>
			</div>

			${
				this.error && this.validationMessage
					? html`<div role="alert" class="mt-1 text-sm text-error-default">${this.validationMessage}</div>`
					: nothing
			}
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-quantity': SchmancyQuantity
	}
}
